/**
 * Lightweight DSGVO/GDPR cookie-consent state, persisted in localStorage.
 * Future tracking integrations (Matomo / Google Analytics) MUST gate their
 * initialization via `consent.statistics === true`.
 */

export interface CookieConsent {
  necessary: true;
  statistics: boolean;
  marketing: boolean;
  decidedAt: string;       // ISO date when the user made a choice
  version: number;         // bump to force re-prompt after policy changes
}

export const COOKIE_CONSENT_KEY = 'mrlw.cookieConsent';
export const COOKIE_CONSENT_VERSION = 1;

export const loadConsent = (): CookieConsent | null => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!raw) return null;
    const parsed: CookieConsent = JSON.parse(raw);
    if (!parsed || parsed.version !== COOKIE_CONSENT_VERSION) return null;
    return parsed;
  } catch {
    return null;
  }
};

export const saveConsent = (statistics: boolean, marketing: boolean): CookieConsent => {
  const consent: CookieConsent = {
    necessary: true,
    statistics,
    marketing,
    decidedAt: new Date().toISOString(),
    version: COOKIE_CONSENT_VERSION,
  };
  if (typeof window !== 'undefined') {
    window.localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(consent));
    // Broadcast so listeners (banner, tracking init) can react in-page.
    window.dispatchEvent(new CustomEvent('mrlw:cookieConsent', { detail: consent }));
  }
  return consent;
};

export const clearConsent = (): void => {
  if (typeof window !== 'undefined') {
    window.localStorage.removeItem(COOKIE_CONSENT_KEY);
    window.dispatchEvent(new CustomEvent('mrlw:cookieConsent', { detail: null }));
  }
};
